import { useEffect, useMemo, useRef, useState } from "react"
import { Box, Button, Flex, HStack, Text, Badge, Input, VStack, chakra } from "@chakra-ui/react"
import SectionCard from "../../framework/SectionCard"
import { API_BASE_URL } from "../../framework/api"
import { formatDateTime } from "../../utils/datetime"
import { extractErrorInfo } from "../../lib/api/error-utils"
import { useAuthToken } from "../../hooks/useAuthToken"
import { 
  usePipelineVersions, 
  useActivatePipelineVersion,
  useImportPipelineVersion,
  useDeletePipelineVersionData
} from "../../hooks/usePipelineVersions"
import ErrorAlert from "../../components/ErrorAlert"

type TableImportResult = {
  table: string
  rows: number
  bytes: number
  durationMs: number
}

type ImportResult = {
  pipelineVersion: string
  ready: boolean
  durationMs: number
  tables: TableImportResult[]
}

type DeleteResult = {
  pipelineVersion: string
  deleted: Record<string, number>
}

type PipelineImportProgressEventData = {
  PipelineVersion: string
  Phase: "downloading" | "copying" | "completed" | "failed"
  Table: string
  BytesTotal: number
  BytesCompleted: number
  Rows?: number
  DurationMs?: number
  Error?: string
  Percent?: number
}

type WsEventPayload<T> = {
  Id: string
  Type: string
  Subject?: string
  Object: T
}

export default function PipelineVersionDashboard() {
  // Auth token from context
  const token = useAuthToken()
  
  // React Query hooks
  const { data: versions = [], isLoading: isLoadingData, error: fetchError } = usePipelineVersions()
  const activateMutation = useActivatePipelineVersion()
  const importMutation = useImportPipelineVersion()
  const deleteMutation = useDeletePipelineVersionData()

  // Local UI state
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionErrorTraceId, setActionErrorTraceId] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [actionInProgress, setActionInProgress] = useState<string | null>(null)
  const [importUid, setImportUid] = useState("")
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [deleteUid, setDeleteUid] = useState("")
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null)
  
  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false)
  const [liveImportUid, setLiveImportUid] = useState<string | null>(null)
  const liveImportUidRef = useRef<string | null>(null)
  const [tableProgress, setTableProgress] = useState<Record<string, PipelineImportProgressEventData>>({})
  const wsRef = useRef<WebSocket | null>(null)

  // Extract error info from fetch error
  const fetchErrorInfo = fetchError ? extractErrorInfo(fetchError) : null

  // Maintain a single websocket connection for streaming import progress
  // Note: This is optional - if it fails to connect, the app will still work
  useEffect(() => {
    const wsUrl = `${API_BASE_URL}/ws?token=${encodeURIComponent(token)}`
    let ws: WebSocket | null = null
    let keepAlive: ReturnType<typeof setInterval> | null = null
    let isCleaningUp = false
    
    // Delay WebSocket creation slightly to avoid React StrictMode double-mount issues
    const connectTimer = setTimeout(() => {
      if (isCleaningUp) return
      
      try {
        ws = new WebSocket(wsUrl.replace(/^http/, "ws").replace(/^https/, "wss"))
        wsRef.current = ws

      keepAlive = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN && !isCleaningUp) {
          try {
            ws.send(JSON.stringify({ type: "keepalive" }))
          } catch (err) {
            // Ignore errors during keepalive
          }
        }
      }, 30000)

      ws.onopen = () => {
        if (!isCleaningUp) {
          setWsConnected(true)
        }
      }
      ws.onclose = () => setWsConnected(false)
      ws.onerror = () => {
        // Silently handle WebSocket errors - this is optional functionality
        setWsConnected(false)
      }
      ws.onmessage = (event) => {
        if (isCleaningUp) return
        try {
          const parsed: WsEventPayload<PipelineImportProgressEventData> = JSON.parse(event.data)
          console.log('[Pipeline WS] Received message:', parsed.Type, JSON.stringify(parsed, null, 2))
          
          if (parsed.Type !== "pipeline_import_progress") {
            console.log('[Pipeline WS] Ignoring - wrong type')
            return
          }

          const payload = parsed.Object
          console.log('[Pipeline WS] Payload:', payload)
          if (!payload) {
            console.log('[Pipeline WS] No payload!')
            return
          }
          const currentUid = liveImportUidRef.current
          console.log('[Pipeline WS] Current UID:', currentUid, 'Payload UID:', payload.PipelineVersion)
          if (currentUid && payload.PipelineVersion !== currentUid) {
            console.log('[Pipeline WS] Ignoring - UID mismatch')
            return
          }

          console.log('[Pipeline WS] Processing progress:', payload.Phase, payload.Table, payload.Percent)
          setLiveImportUid(payload.PipelineVersion)
          liveImportUidRef.current = payload.PipelineVersion
          if (payload.Table && payload.Table !== "all") {
            setTableProgress((prev) => ({
              ...prev,
              [payload.Table]: payload,
            }))
          }

          if (payload.Phase === "failed") {
            setActionError(payload.Error ?? "Import failed")
          }

          if (payload.Phase === "completed" && payload.Table === "all") {
            setActionSuccess(`Import completed for ${payload.PipelineVersion.slice(0, 8)}`)
          }
        } catch (err) {
          console.error('[Pipeline WS] Error parsing message:', err)
        }
      }
      } catch (err) {
        // Silently handle WebSocket creation errors
        setWsConnected(false)
      }
    }, 100) // Small delay to avoid StrictMode issues

    return () => {
      isCleaningUp = true
      clearTimeout(connectTimer)
      if (keepAlive) {
        clearInterval(keepAlive)
      }
      if (ws) {
        try {
          // Only close if we're not already closing/closed
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close()
          }
        } catch {
          // Silently ignore cleanup errors
        }
      }
      wsRef.current = null
      setWsConnected(false)
    }
  }, [token])

  const handleActivate = async (uid: string) => {
    setActionInProgress(uid)
    setActionError(null)
    setActionErrorTraceId(null)
    setActionSuccess(null)
    
    try {
      await activateMutation.mutateAsync(uid)
      setActionSuccess(`Pipeline version ${uid.slice(0, 8)} is now active`)
    } catch (err) {
      const errorInfo = extractErrorInfo(err)
      setActionError(errorInfo.message)
      setActionErrorTraceId(errorInfo.traceId)
    } finally {
      setActionInProgress(null)
    }
  }

  const handleImport = async () => {
    const trimmed = importUid.trim()
    if (!trimmed) {
      setActionError("Enter a pipeline version UID to import")
      return
    }

    setLiveImportUid(trimmed)
    liveImportUidRef.current = trimmed
    setTableProgress({})
    setActionError(null)
    setActionErrorTraceId(null)
    setActionSuccess(null)
    setImportResult(null)

    try {
      const data = await importMutation.mutateAsync(trimmed) as ImportResult
      setImportResult(data)
      setActionSuccess(`Imported pipeline ${data.pipelineVersion.slice(0, 8)} (${data.tables.length} tables)`)
    } catch (err) {
      const errorInfo = extractErrorInfo(err)
      setActionError(errorInfo.message)
      setActionErrorTraceId(errorInfo.traceId)
      setLiveImportUid(null)
      liveImportUidRef.current = null
    }
  }

  const formattedImportDuration = useMemo(() => {
    if (!importResult) return ""
    const seconds = importResult.durationMs / 1000
    if (seconds < 60) return `${seconds.toFixed(1)}s`
    return `${(seconds / 60).toFixed(1)}m`
  }, [importResult])

  const formatBytes = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

  const handleDelete = async () => {
    const trimmed = deleteUid.trim()
    if (!trimmed) {
      setActionError("Enter a pipeline version UID to delete data")
      return
    }

    setActionError(null)
    setActionErrorTraceId(null)
    setActionSuccess(null)
    setDeleteResult(null)

    try {
      const data = await deleteMutation.mutateAsync(trimmed) as DeleteResult
      setDeleteResult(data)
      setActionSuccess(`Deleted data for ${data.pipelineVersion.slice(0, 8)}`)
    } catch (err) {
      const errorInfo = extractErrorInfo(err)
      setActionError(errorInfo.message)
      setActionErrorTraceId(errorInfo.traceId)
    }
  }

  return (
    <SectionCard>
      <Flex justify="space-between" align="center" mb="3">
        <Text fontSize="md">Pipeline Versions</Text>
        <Button size="sm" onClick={() => window.location.reload()} loading={isLoadingData}>
          Refresh
        </Button>
      </Flex>

      <Box mb="4" padding="3" border="1px solid" borderColor="gray.700" borderRadius="md" bg="gray.900">
        <Text fontWeight="semibold" mb="1">
          Import pipeline data
        </Text>
        <Text fontSize="sm" color="gray.300" mb="3">
          Only works for constrained geometries - use the K8s job for Prod.
        </Text>
        <HStack gap="3" align="center">
          <Input
            placeholder="Pipeline version UID"
            value={importUid}
            onChange={(e) => setImportUid(e.target.value)}
            disabled={importMutation.isPending}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                void handleImport()
              }
            }}
          />
          <Button
            size="sm"
            colorPalette="teal"
            loading={importMutation.isPending}
            disabled={importMutation.isPending || importUid.trim() === ""}
            onClick={handleImport}
          >
            Import tables
          </Button>
        </HStack>
        {importMutation.isPending && (
          <Text fontSize="sm" color="gray.300" mt="2">
            Importing tables… this may take a couple of minutes. Progress will update when complete.
          </Text>
        )}
        {liveImportUid && (
          <Box mt="3" p="3" bg="gray.800" borderRadius="md">
            <Flex justify="space-between" align="center" mb="2">
              <Text fontWeight="medium">Live progress for {liveImportUid.slice(0, 8)}</Text>
              <Badge colorPalette={wsConnected ? "green" : "orange"} fontSize="xs">
                WebSocket {wsConnected ? "connected" : "connecting"}
              </Badge>
            </Flex>
            <VStack align="stretch" gap="3">
              {Object.values(tableProgress).length === 0 && (
                <Text fontSize="sm" color="gray.400">
                  Awaiting first update…
                </Text>
              )}
              {Object.values(tableProgress).map((p) => {
                const percent =
                  typeof p.Percent === "number"
                    ? p.Percent
                    : p.BytesTotal > 0
                      ? (p.BytesCompleted / p.BytesTotal) * 100
                      : 0
                return (
                  <Box key={p.Table} bg="gray.900" p="2" borderRadius="md">
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.200">
                        {p.Table}
                      </Text>
                      <HStack gap="2">
                        <Badge colorPalette={p.Phase === "failed" ? "red" : p.Phase === "completed" ? "green" : "blue"} fontSize="xs">
                          {p.Phase}
                        </Badge>
                        <Text fontSize="xs" color="gray.400">
                          {percent.toFixed(1)}%
                        </Text>
                      </HStack>
                    </Flex>
                    <Box mt="1" bg="gray.700" borderRadius="full" h="2" overflow="hidden">
                      <Box
                        h="100%"
                        bg={p.Phase === "failed" ? "red.400" : "teal.400"}
                        width={`${Math.min(100, Math.max(0, percent))}%`}
                        transition="width 0.2s ease"
                      />
                    </Box>
                    {p.Phase === "failed" && p.Error && (
                      <Text fontSize="xs" color="red.200" mt="1">
                        {p.Error}
                      </Text>
                    )}
                  </Box>
                )
              })}
            </VStack>
          </Box>
        )}
        {importResult && (
          <Box mt="3" p="3" bg="gray.800" borderRadius="md">
            <Text fontWeight="medium" mb="1">
              Last import summary
            </Text>
            <Text fontSize="sm" color="gray.200" mb="2">
              {importResult.tables.length} tables in {formattedImportDuration} · ready={importResult.ready ? "yes" : "no"}
            </Text>
            <Box borderBottom="1px solid" borderColor="gray.700" mb="2" />
            <Flex gap="4" wrap="wrap">
              {importResult.tables.map((table) => (
                <Box key={table.table} bg="gray.900" p="2" borderRadius="md" minW="160px">
                  <Text fontSize="sm" color="gray.300">
                    {table.table}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {table.rows.toLocaleString()} rows · {formatBytes(table.bytes)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {(table.durationMs / 1000).toFixed(2)}s
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>
        )}
      </Box>

      <Box mb="4" padding="3" border="1px solid" borderColor="gray.700" borderRadius="md" bg="gray.900">
        <Text fontWeight="semibold" mb="1" color="red.200">
          Delete pipeline data (tables only)
        </Text>
        <Text fontSize="sm" color="gray.300" mb="3">
          Don't do this...
        </Text>
        <HStack gap="3" align="center">
          <Input
            placeholder="Pipeline version UID"
            value={deleteUid}
            onChange={(e) => setDeleteUid(e.target.value)}
            disabled={deleteMutation.isPending}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                void handleDelete()
              }
            }}
          />
          <Button
            size="sm"
            colorPalette="red"
            variant="solid"
            loading={deleteMutation.isPending}
            disabled={deleteMutation.isPending || deleteUid.trim() === ""}
            _disabled={{
              bg: "red.700",
              color: "red.200",
              borderColor: "red.600",
              cursor: "not-allowed",
              opacity: 0.8,
            }}
            onClick={handleDelete}
          >
            Delete data
          </Button>
        </HStack>
        {deleteResult && (
          <Box mt="3" p="3" bg="gray.800" borderRadius="md">
            <Text fontWeight="medium" mb="2">
              Deleted rows for {deleteResult.pipelineVersion}
            </Text>
            <Flex gap="4" wrap="wrap">
              {Object.entries(deleteResult.deleted).map(([table, count]) => (
                <Box key={table} bg="gray.900" p="2" borderRadius="md" minW="160px">
                  <Text fontSize="sm" color="gray.300">
                    {table}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {count.toLocaleString()} rows deleted
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>
        )}
      </Box>

      {fetchErrorInfo && <ErrorAlert message={fetchErrorInfo.message} traceId={fetchErrorInfo.traceId} />}

      {actionError && <ErrorAlert message={actionError} traceId={actionErrorTraceId} />}

      {actionSuccess && (
        <Box mb="4" bg="green.700" px="3" py="2" borderRadius="md">
          <Text fontSize="sm" color="white">
            {actionSuccess}
          </Text>
        </Box>
      )}

      <Box overflowX="auto">
        {isLoadingData ? (
          <Flex align="center" justify="center" py="6">
            <Text fontSize="sm">Loading…</Text>
          </Flex>
        ) : versions.length === 0 ? (
          <Text textAlign="center" fontSize="sm" color="gray.500" py="4">
            No pipeline versions available.
          </Text>
        ) : (
          <chakra.table width="100%">
            <chakra.thead>
              <chakra.tr>
                <chakra.th fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                  VERSION
                </chakra.th>
                <chakra.th fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                  Created At
                </chakra.th>
                <chakra.th fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                  Status
                </chakra.th>
                <chakra.th fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                  Actions
                </chakra.th>
              </chakra.tr>
            </chakra.thead>
            <chakra.tbody>
              {versions.map((version) => (
                <chakra.tr key={version.uid}>
                  <chakra.td px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <Text as="span" fontFamily="mono" fontSize="xs">
                      {version.uid}
                    </Text>
                  </chakra.td>
                  <chakra.td px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    {formatDateTime(version.createdAt)}
                  </chakra.td>
                  <chakra.td px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <HStack gap="2">
                      {version.active && (
                        <Badge colorPalette="green" fontSize="xs">
                          Active
                        </Badge>
                      )}
                      {!version.active && (
                        <Badge colorPalette="gray" fontSize="xs">
                          Inactive
                        </Badge>
                      )}
                    </HStack>
                  </chakra.td>
                  <chakra.td px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <HStack gap="2">
                      {!version.active && (
                        <Button
                          size="xs"
                          colorPalette="blue"
                          onClick={() => handleActivate(version.uid)}
                          loading={actionInProgress === version.uid}
                          disabled={actionInProgress !== null}
                        >
                          Activate
                        </Button>
                      )}
                    </HStack>
                  </chakra.td>
                </chakra.tr>
              ))}
            </chakra.tbody>
          </chakra.table>
        )}
      </Box>
    </SectionCard>
  )
}
